/* eslint-disable react/prop-types */
import {
	Box,
	Typography,
	FormControlLabel,
	Checkbox,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import CustomButton from '../../../../v2/general/CustomButton/CustomButton'
import AddIcon from '@mui/icons-material/Add'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { useEffect, useMemo, useState } from 'react'
import { Services, TimeSheetPageState } from '../../../../../store/slices/timesheet/timesheetPageSlice'
import { getWorkTimePeriods, formatMinutesToHHmm, sumPeriodsMinutes } from '../TimesheetServicesHelpers'
import { WorkTimePeriod, WorkPeriod } from './WorkPeriod'
import CustomTimePicker from '../../../../general/CustomTimePicker/CustomTimePicker'
import CustomTextField from '../../../../general/CustomTextField/CustomTextField'
import CustomDatePicker from '../../../../general/CustomDatePicker/CustomDatePicker'

type InitialStatus = {
	kmsId: number
	kmsIni: number | null
	regDate: string | null
	inEmpty: boolean
	isClean: boolean
}

interface WorkTimesContentProps {
	isModal?: boolean
	workTimes: WorkTimePeriod[]
	initialStatus: InitialStatus | null
	paysExtraTime: boolean
	services: Services[]
	timeSheet: TimeSheetPageState
	filteredTimesheets: Services[]
	totalIntervalWorkTimes: number
	handleStartTimeChange: (newValue: dayjs.Dayjs | null, index: number) => void
	handleStartDateChange: (newValue: dayjs.Dayjs | null, index: number) => void
	handleEndTimeChange: (newValue: dayjs.Dayjs | null, index: number) => void
	handleEndDateChange: (newValue: dayjs.Dayjs | null, index: number) => void
	handleRemovePeriod: (index: number) => void
	handleAddNewPeriod: () => void
	handleExtraTimePayChange: (event: React.SyntheticEvent<Element, Event>) => void
	handleCleanStatusChange: (isClean: boolean) => void
	// Propagate KMS inputs to Redux; without these, changes stay in local state and are lost on save.
	handleStartKmsChange: (value: number) => void
	handleEndKmsChange?: (value: number) => void
}

export const WorkTimesContent: React.FC<Readonly<WorkTimesContentProps>> = ({
	isModal = false,
	workTimes,
	initialStatus,
	paysExtraTime,
	services,
	timeSheet,
	filteredTimesheets,
	totalIntervalWorkTimes,
	handleStartTimeChange,
	handleStartDateChange,
	handleEndTimeChange,
	handleEndDateChange,
	handleRemovePeriod,
	handleAddNewPeriod,
	handleExtraTimePayChange,
	handleCleanStatusChange,
	handleStartKmsChange,
	handleEndKmsChange,
}) => {
	const servicesWithoutReserve = useMemo(() => services.filter(service => !service.service.isReserve), [services])

	const endJourneyKmsValue: number = useMemo(() => {
		const kmsVehicles = servicesWithoutReserve[servicesWithoutReserve.length - 1]?.serviceExecution?.kmsVehicles ?? []
		// Guard against empty array: Math.max(...[]) returns -Infinity
		return kmsVehicles.length === 0 ? 0 : Math.max(...kmsVehicles.map(kmsVehicle => kmsVehicle.kmsEnd ?? 0))
	}, [servicesWithoutReserve])

	const startJourneyKms: number = useMemo(() => {
		const kmsVehicles = servicesWithoutReserve[0]?.serviceExecution?.kmsVehicles ?? []
		// Guard against empty array: Math.min(...[]) returns +Infinity
		return kmsVehicles.length === 0 ? 0 : Math.min(...kmsVehicles.map(kmsVehicle => kmsVehicle.kmsIni ?? 0))
		// Depend on the full array so any item change (not just first/last kmsVehicles ref) triggers recomputation
	}, [servicesWithoutReserve])

	const [isRelaxed, setIsRelaxed] = useState<boolean>(false)
	const [endJourneyKmsInput, setEndJourneyKmsInput] = useState<number>(endJourneyKmsValue)
	const [startJourneyKmsInput, setStartJourneyKmsInput] = useState<number>(startJourneyKms)

	// Sync input state when computed value changes after mount (e.g. async data load)
	useEffect(() => {
		setEndJourneyKmsInput(endJourneyKmsValue)
	}, [endJourneyKmsValue])
	useEffect(() => {
		setStartJourneyKmsInput(startJourneyKms)
	}, [startJourneyKms])

	const totalHours = useMemo(() => {
		const periods = getWorkTimePeriods(workTimes)
		return formatMinutesToHHmm(sumPeriodsMinutes(periods))
	}, [workTimes])

	const totalExtraTime = useMemo(() => {
		const MAX_ALLOWED_MINUTES = 8 * 60 // 8 hours in minutes
		const hoursFromTotalHours = Number(totalHours.split(':')[0])
		const minutesFromTotalHours = Number(totalHours.split(':')[1])
		const totalHoursInMinutes = hoursFromTotalHours * 60 + minutesFromTotalHours

		if (totalHoursInMinutes > MAX_ALLOWED_MINUTES) {
			return formatMinutesToHHmm(totalHoursInMinutes - MAX_ALLOWED_MINUTES)
		}

		return '00:00'
	}, [totalHours])

	// ?? null: when workTimes is empty the picker must show nothing, not the current time that dayjs() would produce.
	const endJourneyDate = useMemo(() => {
		return workTimes[workTimes.length - 1]?.endDate ?? null
	}, [workTimes])

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'flex-start',
						gap: '1rem',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
						}}
					>
						<Typography
							variant='h6'
							sx={{
								width: '400px',
								fontWeight: 600,
								fontSize: isModal ? '1.5rem' : '1rem',
								fontFamily: 'var(--font-family)',
								color: 'var(--primary-dark)',
								lineHeight: '160%',
							}}
						>
							Jornada: Períodos de Trabalho
						</Typography>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '1rem',
							}}
						>
							{workTimes.length < 4 && (
								<CustomButton
									variant='text'
									size='small'
									onClick={handleAddNewPeriod}
								>
									Adicionar Período
									<AddIcon sx={{ width: '16px', height: '16px', marginLeft: '0.125em' }} />
								</CustomButton>
							)}
							{!isModal && (
								<Box
									sx={{
										borderRadius: '4px',
										overflowY: 'visible',
									}}
								>
									<CustomButton
										variant='text'
										size='small'
										aria-label='relaxed-journey-table'
										sx={{
											minWidth: 'unset',
											padding: '4.8px !important',
											borderRadius: 0,
											backgroundColor: isRelaxed ? '#F5F7FC' : 'transparent',
											'&:hover': { backgroundColor: 'inherit' },
										}}
										onClick={() => !isRelaxed && setIsRelaxed(true)}
									>
										<TableRowsIcon sx={{ width: '14px', height: '14px' }} />
									</CustomButton>
									<CustomButton
										variant='text'
										size='small'
										aria-label='compact-journey-table'
										sx={{
											minWidth: 'unset',
											padding: '4.8px !important',
											borderRadius: 0,
											backgroundColor: isRelaxed ? 'transparent' : '#F5F7FC',
											'&:hover': { backgroundColor: 'inherit' },
										}}
										onClick={() => isRelaxed && setIsRelaxed(false)}
									>
										<ViewHeadlineIcon sx={{ width: '14px', height: '14px' }} />
									</CustomButton>
								</Box>
							)}
						</Box>
					</Box>

					<TableContainer
						component={Paper}
						sx={{
							boxShadow: 'none',
							fontFamily: 'var(--font-family)',
							fontSize: '0.75rem',
						}}
					>
						<Table
							aria-label='work-periods-table'
							size={isRelaxed ? 'medium' : 'small'}
						>
							<TableHead>
								<TableRow>
									<TableCell
										sx={{
											fontFamily: 'var(--font-family)',
											fontSize: '0.75rem',
											borderRight: isModal ? '1px solid rgba(134, 149, 175, 0.15)' : '1px solid transparent',
											backgroundColor: isModal ? 'rgba(245, 247, 252, 0.50)' : 'transparent',
											fontWeight: 600,
											lineHeight: '200%',
											letterSpacing: '0.17px',
											color: 'var(--primary-light)',
										}}
									>
										Período
									</TableCell>
									<TableCell
										sx={{
											fontFamily: 'var(--font-family)',
											fontSize: '0.75rem',
											fontWeight: 600,
											lineHeight: '200%',
											letterSpacing: '0.17px',
											color: 'var(--primary-light)',
											paddingLeft: '1.25rem',
										}}
									>
										Hora e Data Início
									</TableCell>
									<TableCell
										sx={{
											fontFamily: 'var(--font-family)',
											fontSize: '0.75rem',
											fontWeight: 600,
											lineHeight: '200%',
											letterSpacing: '0.17px',
											color: 'var(--primary-light)',
											paddingLeft: '1.25rem',
										}}
									>
										Hora e Data Fim
									</TableCell>
									{!isModal && (
										<>
											{
												/* Displays only if any of the services mainSecundaryDriver is not null */
												services.some(service => service.mainSecundaryDriver) && (
													<TableCell
														sx={{
															fontFamily: 'var(--font-family)',
															fontSize: '0.75rem',
															fontWeight: 600,
															lineHeight: '200%',
															letterSpacing: '0.17px',
															color: 'var(--primary-light)',
														}}
													>
														Viatura
													</TableCell>
												)
											}

											<TableCell
												sx={{
													fontFamily: 'var(--font-family)',
													fontSize: '0.75rem',
													fontWeight: 600,
													lineHeight: '200%',
													letterSpacing: '0.17px',
													color: 'var(--primary-light)',
													paddingLeft: '1.25rem',
												}}
											>
												Kms
											</TableCell>

											<TableCell
												colSpan={services.some(service => service.mainSecundaryDriver) ? 2 : 1}
												sx={{
													fontFamily: 'var(--font-family)',
													fontSize: '0.75rem',
													fontWeight: 600,
													lineHeight: '200%',
													letterSpacing: '0.17px',
													color: 'var(--primary-light)',
												}}
											>
												Limpeza
											</TableCell>
										</>
									)}
									<TableCell />
								</TableRow>
							</TableHead>
							<TableBody>
								{!isModal && (
									<TableRow>
										<TableCell
											sx={{
												fontFamily: 'var(--font-family)',
												fontSize: '0.75rem',
												borderRight: '1px solid transparent',
												backgroundColor: 'transparent',
												fontWeight: 400,
												lineHeight: '130%',
												letterSpacing: '0.17px',
												color: 'var(--primary-dark)',
											}}
										>
											Início de Jornada
										</TableCell>
										<TableCell colSpan={2}>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
												<CustomTimePicker
													disabled
													aria-label='Hora de início de jornada'
													value={workTimes[0]?.startTime ?? null}
												/>
												<CustomDatePicker
													disabled
													aria-label={`Data de início de jornada`}
													value={workTimes[0]?.startDate ?? null}
												/>
											</Box>
										</TableCell>
										{
											/* Displays only if any of the services mainSecundaryDriver is not null */
											services.some(service => service.mainSecundaryDriver) && (
												// Vehicle fleet number
												<TableCell
													sx={{
														fontFamily: 'var(--font-family)',
														fontSize: '0.75rem',
														fontWeight: 400,
														lineHeight: '130%',
														letterSpacing: '0.17px',
														color: 'var(--primary-dark)',
													}}
												>
													{services[0].service.vehicle.fleetNumber}
												</TableCell>
											)
										}
										{/* Kms at the start of the journey */}
										<TableCell
											sx={{
												fontFamily: 'var(--font-family)',
												fontSize: '0.75rem',
												fontWeight: 400,
												lineHeight: '130%',
												letterSpacing: '0.17px',
												color: 'var(--primary-dark)',
											}}
										>
											<CustomTextField
												aria-label='Kms de início de jornada'
												required
												size='small'
												type='number'
												value={startJourneyKmsInput}
												defaultValue={0}
												inputProps={{ min: 0 }}
												onChange={e => {
													const value = Number(e.target.value)
													setStartJourneyKmsInput(value)
													handleStartKmsChange(value)
												}}
											/>
										</TableCell>
										{/* Clean status */}
										<TableCell colSpan={services?.some(service => service.mainSecundaryDriver) ? 3 : 2}>
											<Checkbox
												aria-label='Status de limpeza'
												checked={timeSheet.initialStatus?.isClean ?? false}
												disabled={false}
												onClick={e => e.stopPropagation()}
												onChange={e => handleCleanStatusChange((e.target as HTMLInputElement).checked)}
												sx={{
													color: 'var(--primary-light)',
													'&.Mui-checked': {
														color: 'var(--primary)',
													},
												}}
											/>
										</TableCell>
									</TableRow>
								)}
								{workTimes.map((wt, index) => (
									<WorkPeriod
										key={wt._key ?? String(index)} // _key prevents React from reusing the wrong row when a period is removed
										isModal={isModal}
										idx={index}
										workTime={wt}
										workTimes={workTimes}
										services={services}
										timeSheet={timeSheet}
										handleStartTimeChange={handleStartTimeChange}
										handleStartDateChange={handleStartDateChange}
										handleEndTimeChange={handleEndTimeChange}
										handleEndDateChange={handleEndDateChange}
										handleRemovePeriod={handleRemovePeriod}
									/>
								))}
								{!isModal /* Displays only if any of the services mainSecundaryDriver is not null */ && (
									<TableRow>
										<TableCell
											colSpan={2}
											sx={{
												borderRight: '1px solid transparent',
												backgroundColor: 'transparent',
												fontFamily: 'var(--font-family)',
												fontSize: '0.75rem',
												fontWeight: 400,
												lineHeight: '130%',
												letterSpacing: '0.17px',
												color: 'var(--primary-dark)',
											}}
										>
											Fim de Jornada
										</TableCell>
										<TableCell>
											<Box
												sx={{
													display: 'flex',
													alignItems: 'center',
													gap: 0.25,
												}}
											>
												<CustomTimePicker
													disabled
													aria-label='Hora de fim de jornada'
													value={workTimes[workTimes.length - 1]?.endTime ?? null}
												/>
												<CustomDatePicker
													disabled
													aria-label='Data de fim de jornada'
													value={endJourneyDate}
												/>
											</Box>
										</TableCell>
										{
											/* Displays only if any of the services mainSecundaryDriver is not null */
											services.some(service => service.mainSecundaryDriver) && (
												<TableCell
													sx={{
														fontFamily: 'var(--font-family)',
														fontSize: '0.75rem',
														fontWeight: 400,
														lineHeight: '130%',
														letterSpacing: '0.17px',
														color: 'var(--primary-dark)',
													}}
												>
													{filteredTimesheets[filteredTimesheets.length - 1]?.service.vehicle.fleetNumber ?? '—'}
												</TableCell>
											)
										}
										<TableCell
											colSpan={services.some(service => service.mainSecundaryDriver) ? 4 : 3}
											sx={{
												paddingLeft: '1.25rem',
											}}
										>
											<CustomTextField
												aria-label='Kms de fim de jornada'
												required
												size='small'
												type='number'
												value={endJourneyKmsInput}
												defaultValue={0}
												inputProps={{ min: 0 }}
												onChange={e => {
													const value = Number(e.target.value)
													setEndJourneyKmsInput(value)
													handleEndKmsChange?.(value)
												}}
											/>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: '1rem',
								marginTop: '1.5rem',
							}}
						>
							<Typography
								variant='h6'
								sx={{
									fontFamily: 'var(--font-family)',
									fontSize: '0.875rem',
									fontWeight: 600,
									color: 'var(--primary-dark)',
								}}
							>
								Total Horas
							</Typography>

							<CustomTextField
								type='time'
								label='Total Horas'
								size='small'
								value={totalHours}
								disabled
							/>

							{!isModal && (
								<>
									<Typography
										variant='h6'
										sx={{
											fontFamily: 'var(--font-family)',
											fontWeight: 600,
											fontSize: '0.875rem',
											color: 'var(--primary-dark)',
										}}
									>
										Intervalo
									</Typography>

									<CustomTextField
										type='time'
										label='Total Intervalo'
										size='small'
										value={dayjs().startOf('day').add(totalIntervalWorkTimes, 'minute').format('HH:mm')}
										disabled
									/>

									<Typography
										variant='h6'
										sx={{
											fontFamily: 'var(--font-family)',
											fontWeight: 600,
											fontSize: '0.875rem',
											color: 'var(--primary-dark)',
										}}
									>
										Extra
									</Typography>

									<CustomTextField
										type='time'
										label='Total Extra'
										size='small'
										value={totalExtraTime}
										disabled
									/>

									<FormControlLabel
										control={<Checkbox size='small' />}
										aria-label='extratime-payment-checkbox'
										label='A Empresa paga Horas Extra'
										sx={{
											marginLeft: 'auto',
											display: 'flex',
											alignItems: 'center',
											gap: '0.125rem',
											'& .MuiTypography-body1': {
												fontFamily: 'var(--font-family)',
												fontSize: '13px',
												fontWeight: 500,
												lineHeight: '120%',
												color: 'var(--primary-dark)',
											},
											'& .MuiCheckbox-root': {
												color: 'var(--primary-light)',
												'&.Mui-checked': {
													color: 'var(--primary)',
												},
											},
										}}
										checked={paysExtraTime}
										onChange={event => handleExtraTimePayChange(event)}
									/>
								</>
							)}
						</Box>
					</TableContainer>
				</Box>
			</Box>
		</LocalizationProvider>
	)
}
